import React, { useContext, useEffect, useRef, useState } from 'react';
import { Icon } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import Classes from './SellerLocationSection.module.scss';
import { TListingSellerInfo } from 'types/TListing';
import getFormattedLocation from '../../../utils/getFormattedLocation';
import getSellerDistance from '../../../repositories/getSellerDistance';
import EnvironmentContext from '../../../contexts/EnvironmentContext';

let Map;

type TSellerLocationProps = {
  seller: TListingSellerInfo;
};

const SellerLocationSection = ({ seller }: TSellerLocationProps) => {
  const {
    tenantContext: { tenant },
    userDetails: {
      location: { postcode },
    },
  } = useContext(EnvironmentContext);
  const { t } = useI18nContext();
  const sellerLocationSectionRef = useRef<HTMLDivElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [sellerDistance, setSellerDistance] = useState<string>('');
  const formattedLocation = getFormattedLocation(seller.location, t);
  const isNationWide = seller.location && !seller.location.isAbroad && !seller.location.cityName;
  const showCity =
    tenant === 'mp' && !seller.location.latitude && !seller.location.longitude && Boolean(seller.location.cityName);
  const showMap = (seller.location && seller.location.latitude && seller.location.longitude) || showCity;

  useEffect(() => {
    if (!showMap) {
      return;
    }

    const elementRef = sellerLocationSectionRef.current as Element;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            (async () => {
              try {
                Map = (await import(/* webpackChunkName: "map" */ '../../SellerSidebarApp/components/Map/Map')).default;
                setIsLoaded(true);
              } catch {}
            })();
          }
        });
      },
      {
        rootMargin: '0px 0px 100px 0px',
      },
    );

    if (elementRef) {
      observer.observe(elementRef);
    }

    return () => {
      if (elementRef) {
        observer.disconnect();
      }
    };
  }, [showMap]);

  useEffect(() => {
    if (isLoaded) {
      (async () => {
        if (postcode && seller.location.latitude && seller.location.longitude) {
          setSellerDistance(
            await getSellerDistance({
              sellerGeoLocation: [seller.location.latitude, seller.location.longitude],
              postcode,
            }),
          );
        }
      })();
    }
  }, [isLoaded, postcode, seller.location.latitude, seller.location.longitude]);

  return (
    <div className={`hz-Page-section hz-Page-section--separated ${Classes.root}`} ref={sellerLocationSectionRef}>
      <div className={Classes.locationInfo}>
        <Icon className={Classes.locationIcon} name={isNationWide ? 'truck' : 'location'} />
        <div className={Classes.locationText}>
          <div className={`${Classes.locationName} hz-Text hz-Text--bodyLargeStrong`}>{formattedLocation}</div>
          {sellerDistance && (
            <div className={`${Classes.locationDistance} hz-Text hz-Text--bodyLarge`}>{sellerDistance}</div>
          )}
        </div>
      </div>
      {showMap && (
        <div className={Classes.locationMap}>{isLoaded && <Map location={seller.location} showCity={showCity} />}</div>
      )}
    </div>
  );
};

export default SellerLocationSection;
